import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { format, parse, isValid } from "date-fns";

import RestaurantBtn from "./RestaurantBtn";

import { THEME_COLOR, THEME_COLOR3 } from "../constants";
import { allDays } from "./restaurant/AddRestaurant";

import "./../assets/css/custom.css";

import styles from "./AddRestaurantOpeningHours.module.css";

const theme = createTheme({
  palette: {
    gray: {
      main: THEME_COLOR3,
    },
    red: {
      main: "#FF0000",
    },
  },
});

const TimePickerSlotProps = {
  actionBar: {
    sx: {
      "& .MuiButton-root": {
        color: "#000",
        fontFamily: "GilroySemiBold",
      },
    },
  },
  digitalClockSectionItem: {
    sx: {
      fontSize: 13,
      fontFamily: "GilroySemiBold",
      "&.Mui-selected": {
        backgroundColor: `${THEME_COLOR}`,
        color: "#000",
        "&:hover": {
          backgroundColor: `${THEME_COLOR}`,
          color: "#000",
        },
      },
    },
  },
  textField: {
    color: "gray",
    variant: "standard",
    InputLabelProps: {
      style: { fontFamily: "GilroyRegular" },
    },
    inputProps: {
      readOnly: true,
      sx: {
        height: 20,
        fontSize: 12,
        fontFamily: "GilroySemiBold",
        padding: "4px 8px",
      },
    },
    sx: {
      svg: { color: "#000", fontSize: 16 },
      "& .MuiButtonBase-root": {
        width: 24,
        height: 24,
      },
      "& .MuiInputBase-root": {
        minHeight: "32px",
        height: "32px",
      },
      "& .MuiInputLabel-root": {
        fontSize: "12px",
        transform: "translate(14px, 8px) scale(1)",
        "&.MuiInputLabel-shrink": {
          transform: "translate(14px, -9px) scale(0.75)",
        },
      },
    },
  },
};

// Helper: Format Date to time string "HH:mm"
const formatTime = (date) => {
  if (!date || !isValid(date)) return null;
  return format(date, "HH:mm");
};

// Helper: Parse time string to Date
const parseTime = (timeStr) => {
  if (!timeStr) return null;
  const parsed = parse(timeStr, "HH:mm", new Date());
  return isValid(parsed) ? parsed : null;
};

// Helper: Create default day timing object with slots
const createDefaultDayTiming = () => ({
  active: true,
  timings: [{ from: null, to: null }],
});

// Transform openingHourList to slot-based format
const transformToSlotFormat = (openingHourList) => {
  return openingHourList.map((item) => {
    // If item already has timings array from API, use it
    if (item.timings && item.timings.length > 0) {
      return {
        day: item.day,
        active: item.active,
        timings: item.timings,
      };
    }
    // Otherwise, convert from open/close format
    return {
      day: item.day,
      active: item.active,
      timings: item.active && item.open && item.close
        ? [{ from: formatTime(item.open), to: formatTime(item.close) }]
        : [{ from: null, to: null }],
    };
  });
};

// Transform slot-based format back to format with all slots in payload
const transformFromSlotFormat = (slotData) => {
  return slotData.map((item) => {
    const firstSlot = item.timings?.[0];
    return {
      day: item.day,
      active: item.active,
      open: firstSlot?.from ? parseTime(firstSlot.from) : null,
      close: firstSlot?.to ? parseTime(firstSlot.to) : null,
      timings: item.timings || [], // Keep all slots in timings array
    };
  });
};

const AddRestaurantOpeningHours = (props) => {
  // Transform initial data to slot-based format
  const [openingHourList, setOpeningHourList] = useState(() =>
    transformToSlotFormat(props.openingHourList)
  );

  // Toggle day active status
  const onActiveSwitch = (data) => {
    const updatedList = openingHourList.map((item) => {
      if (item.day === data.day) {
        const newActive = !item.active;
        return {
          ...item,
          active: newActive,
          timings: newActive
            ? item.timings.length > 0
              ? item.timings
              : [{ from: null, to: null }]
            : item.timings,
        };
      }
      return item;
    });
    setOpeningHourList(updatedList);
  };

  // Add a new time slot to a day
  const addTimeSlot = (day) => {
    const updatedList = openingHourList.map((item) => {
      if (item.day === day) {
        return {
          ...item,
          timings: [...item.timings, { from: null, to: null }],
        };
      }
      return item;
    });
    setOpeningHourList(updatedList);
  };

  // Remove a time slot from a day
  const removeTimeSlot = (day, slotIndex) => {
    const updatedList = openingHourList.map((item) => {
      if (item.day === day) {
        const newTimings = item.timings.filter((_, idx) => idx !== slotIndex);
        // If no slots left, deactivate the day
        if (newTimings.length === 0) {
          return { ...item, timings: [], active: false };
        }
        return { ...item, timings: newTimings };
      }
      return item;
    });
    setOpeningHourList(updatedList);
  };

  // Update a specific time slot
  const updateTimeSlot = (day, slotIndex, field, value) => {
    const updatedList = openingHourList.map((item) => {
      if (item.day === day) {
        const newTimings = [...item.timings];
        newTimings[slotIndex] = {
          ...newTimings[slotIndex],
          [field]: formatTime(value),
        };
        return { ...item, timings: newTimings };
      }
      return item;
    });
    setOpeningHourList(updatedList);
  };

  // Apply all days times to ALL days
  const handlerOnApplyClick = () => {
    const allDaysItem = openingHourList.find((item) => item.day === allDays);
    if (!allDaysItem || !allDaysItem.timings?.[0]) return;

    const { from, to } = allDaysItem.timings[0];

    const updatedList = openingHourList.map((item) => {
      if (item.day !== allDays) {
        return {
          ...item,
          active: true,
          timings: [{ from, to }],
        };
      }
      return item;
    });
    setOpeningHourList(updatedList);
  };

  // Save button click - transform back to original format
  const onSaveBtnClick = () => {
    const transformedData = transformFromSlotFormat(openingHourList);
    props.resOpeningHours(transformedData);
  };

  // Render time slots for a day
  const renderTimeSlots = (item) => {
    if (!item.timings || item.timings.length === 0) return null;

    return (
      <Grid container spacing={0.5}>
        {/* Header row with From/To labels */}
        <Grid item xs={12}>
          <Grid container spacing={0.5}>
            <Grid item xs={5}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontFamily: "GilroyRegular",
                  color: "#888",
                  mb: 0.5,
                  pl: "14px",
                }}
              >
                From
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontFamily: "GilroyRegular",
                  color: "#888",
                  mb: 0.5,
                  pl: "14px",
                }}
              >
                To
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {item.timings.map((slot, index) => (
          <React.Fragment key={index}>
            <Grid item xs={5}>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label=""
                    value={parseTime(slot.from)}
                    format="hh:mm aa"
                    size="small"
                    onChange={(val) =>
                      updateTimeSlot(item.day, index, "from", val)
                    }
                    slotProps={TimePickerSlotProps}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </Grid>

            <Grid item xs={5}>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label=""
                    value={parseTime(slot.to)}
                    format="hh:mm aa"
                    size="small"
                    onChange={(val) =>
                      updateTimeSlot(item.day, index, "to", val)
                    }
                    slotProps={TimePickerSlotProps}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </Grid>

            {item.timings.length > 1 && (
              <Grid
                item
                xs={2}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => removeTimeSlot(item.day, index)}
                  sx={{ color: "#999" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            )}
          </React.Fragment>
        ))}

        {/* Add slot button - hide for "all days" */}
        {item.day !== allDays && (
          <Grid item xs={12}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => addTimeSlot(item.day)}
              sx={{
                color: THEME_COLOR,
                fontFamily: "GilroySemiBold",
                fontSize: "11px",
                mt: 0.5,
              }}
            >
              Add Time Slot
            </Button>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Grid className={`${styles?.["main-view"]}`}>
      <Grid
        container
        item
        component="form"
        noValidate
        autoComplete="off"
        justifyContent="center"
      >
        {openingHourList.map((item, index) => {
          const isAllDays = item.day === allDays;

          return (
            <Grid
              container
              item
              xs={10}
              key={index}
              className={`${styles?.["open-hrs-view"]} ${
                isAllDays ? styles?.["all-day-view"] : ""
              }`}
              mt={2}
            >
              <Grid container direction="row" item xs={12} md={4} mt={2}>
                <Grid item xs={12} md={6}>
                  <label
                    className="day-label"
                    style={{ fontFamily: "GilroySemiBold" }}
                  >
                    {item.day}
                  </label>
                </Grid>
                <Grid item xs={12} md={6}>
                  {!isAllDays && (
                    <ThemeProvider theme={theme}>
                      <Switch
                        checked={item.active}
                        onChange={() => onActiveSwitch(item)}
                        color="red"
                        size="small"
                        sx={{
                          "& .MuiSwitch-track": {
                            height: 15,
                            backgroundColor: THEME_COLOR3 + " !important",
                            marginTop: -0.3,
                          },
                          "& .MuiSwitch-thumb": {
                            backgroundColor: "#999",
                          },
                          "& .Mui-checked .MuiSwitch-thumb": {
                            backgroundColor: "red !important",
                          },
                        }}
                      />
                    </ThemeProvider>
                  )}
                </Grid>
              </Grid>

              <Grid container direction="row" item xs={12} md={8}>
                {item.active ? (
                  <ThemeProvider theme={theme}>
                    <Grid
                      item
                      xs={12}
                      md={isAllDays ? 8 : 12}
                      sx={{ paddingRight: isAllDays ? 5 : 0, marginTop: { xs: 2, md: 0 } }}
                    >
                      {renderTimeSlots(item)}
                    </Grid>

                    {isAllDays && (
                      <Grid
                        item
                        xs={12}
                        md={4}
                        className={`${styles?.["apply-btn-view"]}`}
                      >
                        <Button
                          disabled={false}
                          type="button"
                          className={`${styles?.["apply-btn"]}`}
                          onClick={handlerOnApplyClick}
                        >
                          Apply All
                        </Button>
                      </Grid>
                    )}
                  </ThemeProvider>
                ) : (
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        fontFamily: "GilroyRegular",
                        fontSize: "11px",
                        color: "#999",
                        mt: 2,
                      }}
                    >
                      Closed
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          );
        })}
      </Grid>

      <RestaurantBtn
        activeTab={2}
        loading={false}
        previousBtnClick={props.previousBtnClick}
        onSaveBtnClick={onSaveBtnClick}
      />
    </Grid>
  );
};

export default AddRestaurantOpeningHours;
